import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatMoneyLedger } from "../lib/formatMoney";
import { currencyLabel } from "../lib/currencies";
import { currenciesOf } from "./charts/currencies";
import { PageLoadError } from "./NoAccess";
import { Loader2, DollarSign, TrendingUp, Users, Receipt, CreditCard, Repeat } from "lucide-react";

/**
 * RevenueDashboard (SuperAdmin /super-admin/revenue).
 * Platform revenue: the commissions earned on tenant transactions. Money of different
 * currencies is never added or compared, so the totals are one line per currency and the
 * table is one group per currency, each figure printed with its own currency code.
 */
export default function RevenueDashboard() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // A failed read is reported as one; it is never drawn as a dashboard of zeros.
  useEffect(() => {
    setLoadError(null);
    api.get("/super-admin/revenue")
      .then(({ data }) => setData(data))
      .catch(setLoadError);
  }, [attempt]);

  if (loadError) return <PageLoadError error={loadError} onRetry={() => setAttempt((n) => n + 1)} testId="revenue-load-error" />;
  if (!data) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>;
  const tot = data.totals;
  const byCurrency = tot.by_currency || {};
  const currencies = currenciesOf([byCurrency], data.settlement_currency).filter((c) => byCurrency[c]);
  const rowsOf = (cur) => data.by_tenant.filter((row) => row.currency === cur);

  return (
    <div data-testid="revenue-dashboard" className="space-y-6">
      <div>
        <h1 className="font-sans text-3xl text-slate-800 flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-amber-600" /> {t("sa.rev.title")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">{t("sa.rev.subtitle")}</p>
      </div>


      <div className="grid sm:grid-cols-4 gap-3">
        <Stat icon={DollarSign} label={t("sa.rev.commissions")} testid="rev-commissions" accent
          value={<PerCurrency currencies={currencies} testid="rev-commissions"
            amount={(cur) => formatMoneyLedger(byCurrency[cur].commission_cents, cur, lang)} />} />
        <Stat icon={TrendingUp} label={t("sa.rev.volume")} testid="rev-volume"
          value={<PerCurrency currencies={currencies} testid="rev-volume"
            amount={(cur) => formatMoneyLedger(byCurrency[cur].volume_cents, cur, lang)} />} />
        <Stat icon={Receipt} label={t("sa.rev.transactions")} value={tot.total_transactions || 0} />
        <Stat icon={Users} label={t("sa.rev.earningTenants")} value={tot.earning_tenants_count || 0}
          hint={t("sa.rev.earningTenantsHint")} testid="rev-earning-tenants" />
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-sans text-xl text-slate-800">{t("sa.rev.topTenants")}</h3>
          <p className="text-xs text-slate-500 mt-1">{t("sa.rev.perCurrencyHint")}</p>
        </div>
        {data.by_tenant.length === 0 ? (
          <p className="text-center text-slate-400 py-12">{t("sa.rev.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.04]">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">{t("sa.rev.colTenant")}</th>
                  <th className="px-3 py-3 text-right">{t("sa.rev.colVolume")}</th>
                  <th className="px-3 py-3 text-right">{t("sa.rev.colCommission")}</th>
                  <th className="px-3 py-3 text-right">{t("sa.rev.colTx")}</th>
                  <th className="px-5 py-3 text-right">{t("sa.rev.colMargin")}</th>
                </tr>
              </thead>
              {currencies.map((cur) => (
                <tbody key={cur} data-testid={`rev-group-${cur}`}>
                  <tr className="border-t border-slate-200 bg-slate-100/60">
                    <th colSpan={5} scope="colgroup" className="px-5 py-2 text-left text-xs uppercase tracking-wider text-slate-500">{currencyLabel(cur)}</th>
                  </tr>
                  {rowsOf(cur).map((row) => {
                    const margin = row.volume_cents > 0 ? Math.round((row.commission_cents / row.volume_cents) * 1000) / 10 : 0;
                    // `tenant_id` null: legacy sales that name no practice (still platform commission).
                    const rowId = row.tenant_id ?? "unattributed";
                    return (
                      <tr key={rowId} className="border-t border-slate-100 hover:bg-white" data-testid={`rev-row-${rowId}-${cur}`}>
                        <td className="px-5 py-3 text-slate-800">{row.tenant_id == null ? t("sa.rev.unattributed") : (row.name || row.tenant_id)}</td>
                        <td className="px-3 py-3 text-right text-slate-800/70">{formatMoneyLedger(row.volume_cents, row.currency, lang)}</td>
                        <td className="px-3 py-3 text-right font-medium text-amber-600">{formatMoneyLedger(row.commission_cents, row.currency, lang)}</td>
                        <td className="px-3 py-3 text-right text-slate-500">{row.tx_count}</td>
                        <td className="px-5 py-3 text-right text-slate-800/70">{margin}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, hint, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 ${accent ? "bg-sol-gold/10 border-sol-gold/30" : "bg-white/[0.04] border-slate-200"}`}>
      <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-sans text-2xl text-slate-800">{value}</div>
      {hint && <p className="text-xs text-slate-500 mt-1" data-testid={`${testid}-hint`}>{hint}</p>}
    </div>
  );
}

/** One line per currency, or a dash when the platform has earned nothing yet. */
function PerCurrency({ currencies, testid, amount }) {
  if (currencies.length === 0) return "—";
  return (
    <div className="space-y-1 text-lg">
      {currencies.map((cur) => <div key={cur} data-testid={`${testid}-${cur}`}>{amount(cur)}</div>)}
    </div>
  );
}
